import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
} from '@angular/common/http';
import { Observable, from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { BasicDialogComponent } from '../shared/components/basic-dialog/basic-dialog.component';
import config from '../../config';
import { AuthService } from '../core/auth/services/auth.service';
import { SimpleDialog } from '../shared/models/simple-dialog';

/**
 * Paths served by the app itself, which must never carry a bearer token.
 *
 * This is an explicit allow-list of path prefixes rather than a substring test.
 * `url.includes('/auth/')` also matches a protected API route such as
 * `/todos/auth/reauthorise`, which would silently strip authentication from it.
 */
const PUBLIC_PATH_PREFIXES = ['/auth/', '/login', '/assets/'] as const;

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  constructor(
    private readonly dialog: MatDialog,
    public router: Router,
    private readonly authService: AuthService,
  ) {}

  /**
   * True when the request targets the protected API and therefore requires a
   * bearer token.
   *
   * `API_GATEWAY_ENDPOINT` is injected at build time and falls back to `""`.
   * A naive `url.includes(config.API_GATEWAY_ENDPOINT)` is then always true,
   * because every string contains the empty string, so a misconfigured build
   * would route every request - including public ones - through the auth flow.
   */
  private isApiRequest(url: string): boolean {
    const endpoint = config.API_GATEWAY_ENDPOINT;
    if (!endpoint) {
      return false;
    }
    try {
      const target = new URL(url, globalThis.location.origin);
      const api = new URL(endpoint, globalThis.location.origin);
      const basePath = api.pathname.replace(/\/$/, '');
      return (
        target.origin === api.origin &&
        (target.pathname === basePath ||
          target.pathname.startsWith(`${basePath}/`))
      );
    } catch {
      return false;
    }
  }

  private isPublicPath(url: string): boolean {
    let path = url;
    try {
      const target = new URL(url, globalThis.location.origin);
      if (target.origin !== globalThis.location.origin) {
        return false;
      }
      path = target.pathname;
    } catch {
      // Relative or malformed URL - fall back to matching the raw value.
    }
    return PUBLIC_PATH_PREFIXES.some((prefix) => path.startsWith(prefix));
  }

  /**
   * Stops the request and sends the user back through login.
   *
   * Rejecting the stream means the request is never dispatched. Dispatching it
   * without an `Authorization` header produces a 401 from the API Gateway
   * Cognito authorizer, and because API Gateway error responses carried no CORS
   * headers the browser discarded them - surfacing to the user as an opaque
   * "0 Unknown Error" instead of an expired session.
   */
  private rejectAndReauthenticate(): Observable<never> {
    const simpleDialog: SimpleDialog = {
      uiTitle: 'Your session has expired',
      uiMessage:
        "To continue, please click 'OK' to be redirected to the login page.",
      redirect: '/auth/logout',
    };
    this.dialog.open(BasicDialogComponent, { data: simpleDialog });
    return throwError(() => new Error('Authentication required.'));
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const originalUrl = req.url;

    // Public assets and the auth flow itself must not carry a token.
    if (this.isPublicPath(originalUrl) || !this.isApiRequest(originalUrl)) {
      return next.handle(req);
    }

    return from(this.authService.getTokens()).pipe(
      catchError(() => this.rejectAndReauthenticate()),
      switchMap(({ accessToken }) => {
        return next.handle(
          req.clone({
            headers: req.headers.set('Authorization', accessToken),
          }),
        );
      }),
    );
  }
}
